import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack, Typography } from '@mui/material';
import type { DataCatalogFieldTip } from 'modules/datacatalog/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import FieldTipAccordion from './FieldTipAccordion';

type TipItem = DataCatalogFieldTip & { id: string };

const toTipItems = (tips: DataCatalogFieldTip[]): TipItem[] =>
    tips.map((tip, i) => ({ ...tip, id: `tip-${i}` }));

const FieldTips = ({
    canEdit,
    tips: initialTips = [],
    onSave,
}: {
    canEdit: boolean;
    tips?: DataCatalogFieldTip[];
    onSave: (tips: TipItem[]) => void;
}) => {
    const { t } = useTranslation();
    const [tips, setTips] = useState<TipItem[]>(toTipItems(initialTips ?? []));

    const addTip = () => {
        const newTip: TipItem = {
            id: `tip-${Date.now()}`,
            question: t('dataCatalog.newQuestion'),
            answer: '',
        };
        const updated = [...tips, newTip];
        setTips(updated);
    };

    if (tips.length === 0 && !canEdit) {
        return null;
    }
    return (
        <Box sx={{ mt: 4, mb: 4 }}>
            <Stack direction="row" sx={{ alignItems: 'center' }}>
                <Typography variant="h5" color="textPrimary">
                    {t('dataCatalog.usageTips')}
                </Typography>
            </Stack>
            <Box>
                {tips.map((tip) => {
                    return (
                        <FieldTipAccordion
                            key={tip.id}
                            tip={tip}
                            canEdit={canEdit}
                            onSave={(newTip) => {
                                const updated = tips.map((tipToUpdate) =>
                                    tipToUpdate.id === newTip.id
                                        ? newTip
                                        : tipToUpdate,
                                );
                                setTips(updated);
                                onSave(updated);
                            }}
                            onDelete={(deletedTip) => {
                                const updated = tips.filter(
                                    (tipToUpdate) =>
                                        tipToUpdate.id !== deletedTip.id,
                                );
                                setTips(updated);
                                onSave(updated);
                            }}
                        />
                    );
                })}
            </Box>
            {canEdit && (
                <Button
                    startIcon={<AddIcon />}
                    onClick={addTip}
                    size="small"
                    sx={{ mt: 1 }}
                >
                    {t('dataCatalog.addTip')}
                </Button>
            )}
        </Box>
    );
};

export default FieldTips;
